import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {Router} from "@angular/router";
import {CalculationService, DealInsuranceService, DealService, LeaseCalculationService, VehicleService} from "../../services";
import {Accessory, FinancingSettings, InsuranceProduct} from "../../models";
import {LeaseDefault, TermCost} from "src/app/settings-module/models";
import {combineLatest, Observable, of, Subject} from "rxjs";
import {LeaseInsuranceProductKeys} from "../../models/insurance";
import {takeUntil} from "rxjs/operators";
import {DealState} from "../../store/state";
import {clone} from "ramda";
import { FeatureFlag, FeatureFlagsService } from "../../services/feature-flags.service";

@Component({
  selector: "app-buy-box-lease",
  templateUrl: "./buy-box-lease.component.html",
  styleUrls: ["./buy-box-lease.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuyBoxLeaseComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject();

  @Input() set insuranceProducts(list: InsuranceProduct[]) {
    this._initialList = list;
    this.initInsuranceProducts(list);
  }

  @Input() financeSettings: FinancingSettings;
  @Input() leaseDefault: LeaseDefault;
  @Input() leaseResiduals = [];
  @Input() leasingTerm: number;
  totalEquity: number;
  totalEquityPercent: number;
  @Output() submitDisabledProducts = new EventEmitter<InsuranceProduct[]>();
  @Output() gapQuestion = new EventEmitter<InsuranceProduct>();
  @Input() disabledProducts: InsuranceProduct[] = [];
  initialSelectedProducts: InsuranceProduct[] = [];
  deal: DealState;
  moneyFactor$: Observable<number>;
  moneyFactorType$: Observable<"CUSTOM" | "INCENTIVE" | "STANDARD">;
  uiStateInsuranceProducts: InsuranceProduct[];
  private selectedProducts: InsuranceProduct[] = [];
  accessories: Accessory[] = [];
  _initialList;

  moveXpelAndDataDotsFlag: boolean = false;

  constructor(
    private calculationService: CalculationService,
    private leaseCalculationService: LeaseCalculationService,
    private vehicleService: VehicleService,
    private dealService: DealService,
    private router: Router,
    private featureFlagsService: FeatureFlagsService,
    private dealInsuranceService: DealInsuranceService,
  ) {
  }

  ngOnInit() {
    this.subToDeal();
    this.moneyFactor$ = this.leaseCalculationService.moneyFactor$(this.leasingTerm);
    this.moneyFactorType$ = this.leaseCalculationService.moneyFactorType$(this.leasingTerm);

    this.moveXpelAndDataDotsFlag = this.featureFlagsService.getFeatureFlag(FeatureFlag.MoveXpelAndDataDots);
    this.featureFlagsService.onFlagUpdated(FeatureFlag.MoveXpelAndDataDots, (value) => {
      this.moveXpelAndDataDotsFlag = value;
    });

    combineLatest([
      this.dealService.selectDeal(),
      this.vehicleService.selectVehicle(),
      this.dealService.selectAccessories()
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([deal, vehicle, accessories]) => {
        const products = Object.assign([], vehicle.insuranceProducts);
        this.initInsuranceProducts(products);
        this.accessories = accessories;
      })
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  gapQuestionClicked(product: InsuranceProduct): void {
    this.gapQuestion.emit(product)
  }

  get leaseOptions() {
    return this.deal.leaseOptions;
  }

  get financeOptions() {
    return this.deal.financeOptions;
  }

  get insuranceProductsTotal(): number {
    let total = 0;
    this.insuranceProducts.forEach(product => total += this.productPrice(product));
    return total;
  }

  get insuranceProducts() {
    return this.uiStateInsuranceProducts;
  }

  initInsuranceProducts(insuranceProducts: InsuranceProduct[]) {
    insuranceProducts = insuranceProducts.filter(product => {
      return Object.values(LeaseInsuranceProductKeys)
        .includes(product.productKey);
    });
    insuranceProducts = this.dealInsuranceService.orderInsuranceProducts(insuranceProducts);
    this.uiStateInsuranceProducts = insuranceProducts;
  }

  private subToDeal() {
    combineLatest([
      this.dealService.selectDeal(),
      this.leaseCalculationService.calcGrossCapCost$()
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([deal, grossCapCost]) => {
        this.deal = deal;
        if (this.deal.insuranceProducts) {
          this.selectedProducts = this.deal.insuranceProducts;
          this.initialSelectedProducts = this.selectedProducts;
        }
        this.totalEquity = this.calculationService.calcTradeEquity(this.deal, 'lease')
        this.totalEquityPercent = this.totalEquity / grossCapCost
        /*this.calculationService.logManager.consoleLog(
          `Lease Buy Box: ${this.leasingTerm} term`, {
            totalEquity: this.totalEquity,
            grossCapCost,
            totalEquityPercent: this.totalEquityPercent,
          })*/
      });
  }

  // UI CONTROL & RENDERING

  onSelectPaymentPlan() {
    this.dealService.setLeasingTerm(this.leasingTerm);
    this.dealService.dispatchSetDealType("lease");
    this.selectedProducts = this.selectedProducts ? this.selectedProducts.map((product: InsuranceProduct) => {
      return this.dealInsuranceService.selectTerm(product, this.leasingTerm);
    }) : [];
    this.dealInsuranceService.dispatchSetInsuranceProducts(this.selectedProducts);
    this.router.navigate([{outlets: {modal: "submit-writeup"}}]);
  }

  paymentPlanSelected(): boolean {
    if (!this.leaseOptions) {
      return false;
    }
    return (
      this.leaseOptions.leaseSelected &&
      this.leasingTerm === this.leaseOptions.selectedLeaseTerm
    );
  }

  productSelected(productKey: string): boolean {
    const product = this.selectedProducts.find(item => {
      return item.productKey === productKey;
    });
    return product ? true : false;
  }

  enableProduct(product: InsuranceProduct) {
    return;
    // const disabledProduct = this.disabledProducts.find((disabledProduct: InsuranceProduct) => {
    //   return disabledProduct.productKey === product.productKey;
    // });
    // if (!disabledProduct) {
    //   this.dealService.Insurance.enableUninitializedProduct(
    //     this.insuranceProducts,
    //     product,
    //     this.leasingTerm
    //   );
    //   return;
    // }
    // const disabledProductIndex = this.disabledProducts.findIndex((disProduct: InsuranceProduct) => {
    //   return product.productKey === disProduct.productKey;
    // });
    // if (disabledProductIndex >= 0) {
    //   this.disabledProducts.splice(disabledProductIndex, 1);
    // }
    // this.submitDisabledProducts.emit(this.disabledProducts);
    // this.dealService.Insurance.submitInsuranceProduct(disabledProduct, disabledProduct.selectedTerm);
  }

  disableProduct(product: InsuranceProduct) {
    return;
    // const selectedProduct = this.selectedProducts.find(item => {
    //   return item.productKey === product.productKey;
    // });
    // this.disabledProducts.push(selectedProduct);
    // this.submitDisabledProducts.emit(this.disabledProducts);
    // this.dealService.Insurance.removeInsuranceProduct(product);
  }

  productPrice(product: InsuranceProduct): number {
    const selectedProduct = this.selectedProducts.find(item => {
      return item.productKey === product.productKey;
    });
    if (!selectedProduct) {
      return 0;
    }

    const {selectedTerm} = selectedProduct;
    const termCost = product.termCosts[selectedTerm];

    return termCost ? termCost.price : 0;
  }

  productDescription(product: InsuranceProduct): string {
    product = clone(product);
    product = this.dealInsuranceService.setHardcodedTerm(product, this.leasingTerm);
    const selectedProduct = this.selectedProducts.find(item => {
      return item.productKey === product.productKey;
    });
    if (!selectedProduct) {
      return "";
    }

    const {selectedTerm} = selectedProduct;
    const selectedTermCost = selectedProduct.termCosts[selectedTerm];
    const termCostIndex = product.termCosts.findIndex((termCost: TermCost) => {
      return selectedTermCost?.term === termCost?.term && selectedTermCost?.miles === termCost?.miles;
    });
    const termCost = product.termCosts[termCostIndex];
    if (product.productKey.toLowerCase() === "gap") {
      // Issue #1958: Remove GAP product description
      return "";
      /*
      const { selectedTerm } = product;
      const termCost = product.termCosts[selectedTerm];
      return "For the life of the loan";
      */
    }

    if (termCost) {
      return `${termCost.term / 12} years / ${termCost.miles} miles`;
    } else {
      return "";
    }
  }

  calculateInsuranceProductMonthlyPaymentForLease$(product: InsuranceProduct): Observable<number> {
    product = clone(product);
    product = this.dealInsuranceService.setHardcodedTerm(product, this.leasingTerm);
    const selectedProduct = clone(this.selectedProducts).find(item => {
      return item.productKey === product.productKey;
    });
    if (!selectedProduct) {
      return of(0);
    }

    const {selectedTerm} = selectedProduct;
    const selectedTermCost = selectedProduct.termCosts[selectedTerm];
    const termCostIndex = product.termCosts.findIndex((termCost: TermCost) => {
      return selectedTermCost?.term === termCost?.term && selectedTermCost?.miles === termCost?.miles;
    });

   // if (product.productKey === "SELECT")
   // console.log("termCostIndex", termCostIndex)

    product.selectedTerm = termCostIndex;

    return this.leaseCalculationService
      .calculateInsuranceProductMonthlyPaymentForLease$(product, this.leasingTerm);
  }

  residualValue$(leasingTerm): Observable<number> {
    return this.leaseCalculationService.residualValue$(leasingTerm);
  }

  calcBaseMonthlyPayment$(): Observable<number> {
    if (!this.inputsAvailable) {
      return of(0);
    }
    return this.leaseCalculationService.calcBaseMonthlyLeasePayment$({term: this.leasingTerm});
  }

  calculateAccessoryMonthlyPayment$(accessory: Accessory): Observable<number> {
    return this.leaseCalculationService.calculateAccessoryMonthlyPaymentForLease$(accessory, this.leasingTerm);
  }

  calcTotalMonthlyPayment$(): Observable<number> {
    if (!this.inputsAvailable) {
      return of(0);
    }
    return this.leaseCalculationService.calcTotalMonthlyLeasePayment$({term: this.leasingTerm});
  }

  navToSimplePayment() {
    this.router.navigate([{outlets: {modal: "simple-payment-calculator"}}]);
  }

  inputsAvailable(): boolean {
    return (
      !!this.leasingTerm &&
      !!this.insuranceProducts &&
      !!this.financeOptions &&
      !!this.financeSettings &&
      !!this.leaseOptions &&
      !!this.leaseDefault
    );
  }

  customResidual() {
    if (this.leaseOptions.customLeaseEnabled && this.leaseOptions.customLeaseResidual) {
      return this.leaseOptions.customLeaseResidual;
    }
    return 0;
  }
}
